<template>
  <b-modal
    :id="modalId"
    no-close-on-backdrop
    size="xl"
    @shown="init()"
    @hidden="hideModal"
  >
    <template v-slot:modal-header>
      <div>
        <h5 class="modal-title">{{ modalTitle }}</h5>
        <div>{{ modalSubtitle }}</div>
      </div>
    </template>
    <name-input
      :all-des="allDes"
      :is-ready="!loadingData"
      :should-focus="!de.name"
      :name="de.name"
      :de-id="de.id"
      @on-name-input-changed="onDeNameChanged"
    ></name-input>

    <element-description
      v-if="!loadingData"
      :elem-description="dataElement.description"
      @on-element-description-changed="onDeDescriptionChanged"
    ></element-description>

    <div class="crm-fields-container" v-if="!loadingData">
      <label class="query-label query-object">
        {{ objectText }}
      </label>
      <div class="obj-select">
        <search-box
          v-if="!fetchObjectsFailed"
          :has-icon="false"
          :box-type="'deEditModal'"
          :list="filteredObjects"
          :value="dataElement.obj ? dataElement.obj : null"
          @on-search-box-item-selected="onObjectSearchItemSelected"
        />

        <button
          v-else
          class="modal-btn modal-save-btn retry-btn"
          :disabled="loadingData"
          @click="describeObjects"
        >
          {{ retryText }}
        </button>
      </div>
    </div>

    <div class="crm-fields-container" v-if="fieldsReady && !loadingData">
      <label class="query-label">
        {{ fieldsText }}
      </label>
      <div class="crm-fields-search">
        <search-box
          v-if="!fetchFieldsFailed"
          :multi-select="true"
          :box-type="'deEditModal'"
          :has-icon="true"
          :list="fieldsForMultiSelect"
          :values="selectedItems"
          @on-search-box-item-selected="onSearchBoxItemSelected"
        />

        <button
          v-else
          class="modal-btn modal-save-btn retry-btn"
          :disabled="loadingData"
          @click="describeSelectedObject(dataElement.obj)"
        >
          {{ retryText }}
        </button>
      </div>
    </div>

    <div class="loader" v-if="loadingData"></div>

    <template v-slot:modal-footer>
      <button
        class="modal-btn no-user-select modal-cancel-btn"
        @click="hideModal()"
      >
        {{ cancelText }}
      </button>
      <button
        class="modal-btn no-user-select modal-save-btn"
        @click="finishQuery()"
        :disabled="isDisabled"
      >
        {{ okText }}
      </button>
    </template>
  </b-modal>
</template>

<script>
  import { mapState } from "vuex";
  import SearchBox from "@/components/SearchBox";
  import services from "@/services";
  import { MUTATION_NAME, TOAST_CONFIG } from "@/static/global-variables";
  import store from "@/store";
  import utils from "@/utils";
  import NameInput from "@/components/NameInput";
  import ElementDescription from "@/components/ElementDescription";

  export default {
    name: "FieldTranslationEdit",
    components: {
      SearchBox,
      NameInput,
      ElementDescription
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      modalId() {
        return `field-translation-edit-modal_${this.de.id}`;
      },
      fieldsForMultiSelect() {
        return this.fields.filter(field => {
          return !field.hideFromMultiSelect;
        });
      },
      objectText() {
        return this.getMessageText(this.messages.OBJECT);
      },
      fieldsText() {
        return this.$t("fields");
      },
      retryText() {
        return this.getMessageText(this.messages.RETRY);
      },
      okText() {
        return this.getMessageText(this.messages.OK);
      },
      cancelText() {
        return this.getMessageText(this.messages.CANCEL);
      },
      modalTitle() {
        return `${this.getMessageText(this.messages.Edit)} ${this.$t(
          utils.getDataElementLabel(this.de.type)
        )}`;
      },
      modalSubtitle() {
        return utils.getModalSubtitle(this.de.type);
      },
      isDisabled() {
        return (
          this.isRequiredFilled(this.dataElement) ||
          this.loadingData ||
          !this.isNameValid
        );
      }
    },
    props: {
      appId: String,
      de: Object,
      id: String,
      name: String,
      allDes: Object
    },
    data() {
      return {
        filteredObjects: [],
        selectedItems: [],
        fields: [],
        loadingData: false,
        fieldsReady: false,
        fetchObjectsFailed: null,
        fetchFieldsFailed: null,
        dataElement: {},
        isNameValid: false
      };
    },
    methods: {
      hideModal() {
        if (this.de.notReady) {
          const params = {
            appId: this.de.appId,
            deId: this.de.id
          };
          store.commit(MUTATION_NAME.DELETE_DATA_ELEMENT, params);
        }
        this.$bvModal.hide(this.modalId);
      },
      init() {
        this.dataElement = JSON.parse(JSON.stringify(this.de));
        let obj = this.dataElement.obj;

        if (!obj) {
          this.describeObjects();
        } else {
          if (this.dataElement.fields) {
            this.selectedItems = Object.keys(this.dataElement.fields).map(
              key => {
                return {
                  name: key,
                  type: this.dataElement.fields[key].dataType,
                  dataType: this.dataElement.fields[key].dataType,
                  label: this.dataElement.fields[key].label
                };
              }
            );

            this.describeSelectedObject(obj);
          } else {
            if (obj.name) {
              this.describeSelectedObject(obj);
            } else {
              this.fields = [];
              this.fieldsReady = false;
            }
          }
        }
      },
      describeObjects() {
        let component = this;

        this.loadingData = true;
        services.fetchAvailableObjects().then(
          res => {
            component.filteredObjects = res;
            this.loadingData = false;
          },
          () => {
            component.fetchObjectsFailed = true;
            component.loadingData = false;

            component.$bvToast.toast(
              this.getMessageText(component.messages.SFDC_METADATA_ERROR),
              TOAST_CONFIG.BOTTOM_CENTER_ERROR
            );
          }
        );
      },

      describeSelectedObject(obj) {
        const component = this;
        component.loadingData = true;

        function errorHandler() {
          component.fieldsReady = true;
          component.loadingData = false;
          component.fetchFieldsFailed = true;
          component.$bvToast.toast(
            this.getMessageText(component.messages.SFDC_METADATA_ERROR),
            TOAST_CONFIG.BOTTOM_CENTER_ERROR
          );
        }

        services.describeObject(obj.name).then(resp => {
          if (resp) {
            services.fetchObjectMetaData(obj.name).then(meta => {
              if (Array.isArray(meta.records)) {
                component.fields = utils.mergeMetadataIntoFields(
                  resp.fields,
                  meta.records,
                  true
                );
                component.fieldsReady = true;
                component.loadingData = false;
              } else {
                errorHandler();
              }
            }, errorHandler);
          } else {
            errorHandler();
          }
        }, errorHandler);
      },
      onSearchBoxItemSelected(selectedItems) {
        this.selectedItems = [...selectedItems];
      },
      onObjectSearchItemSelected(obj) {
        this.$set(this.dataElement, "obj", obj);

        if (obj && obj.name) {
          this.describeSelectedObject(obj);
        } else {
          this.describeObjects();
          this.fields = [];
          this.selectedItems = [];
          this.fieldsReady = false;
        }
      },
      isRequiredFilled(de) {
        return !de.obj || this.selectedItems.length === 0;
      },
      finishQuery() {
        let formattedFields = {};

        this.selectedItems.forEach(item => {
          formattedFields[item.name] = {
            dataType: item.type,
            label: item.label
          };
        });
        let de = {
          id: this.dataElement.id,
          type: this.dataElement.type,
          description: this.dataElement.description,
          name: this.dataElement.name,
          object: this.dataElement.obj,
          fields: formattedFields,
          obj: this.dataElement.obj
        };

        let deInfo = {
          appId: this.appId,
          de
        };
        store.commit(MUTATION_NAME.UPDATE_DE_QUERY, deInfo);
        this.$bvModal.hide(this.modalId);
      },
      onDeNameChanged(isValidName, nameInput) {
        this.dataElement.name = nameInput;
        this.isNameValid = isValidName;
      },
      onDeDescriptionChanged(input) {
        this.dataElement.description = input;
      }
    }
  };
</script>

<style scoped>
  .crm-fields-search {
    flex: 3;
  }
</style>
